/**
 * Responsibility: describe the suburbia biome — gentle ground, a street grid, and the buildings of a small town.
 *
 * ## The gap this fills
 *
 * The world had a downtown and it had open country, and nothing between them: a city stopped and fields began.
 * This is what a city actually has around it.
 *
 * It also needed almost no new machinery. A house is a pitched-roof box, which has existed since farmland's
 * barns, so the common case was already built — see `townGeometry.js` for the handful of things a town has that
 * a farm does not.
 *
 * ## The grid, and why blocks rather than plots decide everything expensive
 *
 * Plots are laid on a world-aligned grid and grouped into blocks. Only the **perimeter** plots of a block are
 * built on, which is what a real block looks like from above and what makes the street grid legible without
 * drawing much. The middle of a block is gardens.
 *
 * Every expensive decision is made per *block*, not per plot:
 *
 * - The **reservation test** is one call for a whole block, following farmland's whole-field precedent. A block
 *   is under 300 units across, so if it is clear of another biome's structures then so is every house in it —
 *   and this is the difference between a few dozen clearance queries a tile and a few hundred.
 * - The **slope test** likewise. A town on a hillside is wrong as a whole, not house by house, and a partly
 *   built block would read as an artefact.
 * - **Civic buildings** are rolled per block and are mutually exclusive, so a block holds a church *or* a school
 *   *or* a water tower *or* houses. That is what keeps them rare without a second placement system.
 *
 * ## Streets are drawn, where the city's are not
 *
 * A downtown explains itself through the gaps between tall towers. A town's buildings are small and low, so from
 * altitude the arrangement is the only signal. Each block draws only its own two low-side edges — farmland's
 * hedgerow rule — so no line is ever drawn twice.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import {
  churchSegments,
  fieldSegments,
  schoolSegments,
  streetSegments,
  waterTowerSegments,
} from '../townGeometry.js';
import { SUBURB, PALETTE, SUBURB_PALETTE } from '../../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/** World size of one block. */
const BLOCK_SIZE = SUBURB.PLOT_SIZE * SUBURB.BLOCK_PLOTS;

/**
 * How far a block's contents can reach from its centre, in world units.
 *
 * **The half-*diagonal*, not the half-width**, plus whatever the largest civic building adds. A block is square
 * and its streets run along the edges, so its corners sit `BLOCK_SIZE / √2` from the centre — using the half-width
 * meant a clearance circle that missed the corners entirely, and the nothing-stands-in-water test caught streets
 * running through a lake because of it.
 *
 * Derived from the geometry rather than written down, so growing a civic building cannot leave the reservation and
 * the clearance test disagreeing about how far a block reaches.
 */
const BLOCK_REACH =
  BLOCK_SIZE * Math.SQRT1_2 +
  Math.max(
    SUBURB.CHURCH_NAVE_LENGTH / 2 + SUBURB.CHURCH_TOWER_WIDTH,
    SUBURB.SCHOOL_LENGTH / 2,

    // The school's pitch is offset clear of the range, so its far edge is the furthest thing a block holds.
    SUBURB.SCHOOL_WIDTH / 2 + SUBURB.FIELD_WIDTH + SUBURB.FIELD_LENGTH / 2,
  );

/**
 * Whether another biome has reserved any of a block's ground.
 *
 * Nine probes on a three-by-three grid over the block, each with a radius of about half the spacing so the discs
 * overlap. **Not one query with a big radius**, because a clearance test does not scale that way: the water
 * clearance checks a centre and a ring at the radius it is given, so a large radius leaves everything between the
 * two unexamined — which is how a street ended up crossing a lake. Farmland's fields hit this first.
 *
 * @param {number} minX - Block's minimum corner.
 * @param {number} minZ
 * @param {(x: number, z: number, radius: number) => boolean} [isReserved]
 */
function blockIsReserved(minX, minZ, isReserved) {
  if (typeof isReserved !== 'function') return false;

  const reach = BLOCK_SIZE / 4 + SUBURB.CLEARANCE;

  for (let i = 0; i <= 2; i += 1) {
    for (let j = 0; j <= 2; j += 1) {
      if (isReserved(minX + (BLOCK_SIZE * i) / 2, minZ + (BLOCK_SIZE * j) / 2, reach)) return true;
    }
  }

  return false;
}

/** How far the school's pitch stands off the range it belongs to, in world units. */
const FIELD_GAP = 14;

/**
 * Clear space a civic building itself needs, in world units.
 *
 * The largest of the three, so one number serves all of them — a church and a water tower simply get a little more
 * room than they need, which costs nothing and keeps this a single query.
 */
const CIVIC_RADIUS = Math.max(
  SUBURB.CHURCH_NAVE_LENGTH / 2 + SUBURB.CHURCH_TOWER_WIDTH,
  SUBURB.SCHOOL_LENGTH / 2,
  SUBURB.TOWER_TANK_RADIUS + SUBURB.TOWER_LEG_SPREAD,
);

/** Range of block indices whose centres fall inside a span. */
function blockRange(start, length) {
  return {
    first: Math.ceil(start / BLOCK_SIZE - 0.5),
    last: Math.ceil((start + length) / BLOCK_SIZE - 0.5) - 1,
  };
}

/**
 * The plots on a block's perimeter, as offsets from the block's minimum corner in plot units.
 *
 * Computed once: it is the same set for every block in the world, and deriving it per block would be a small
 * allocation on a path that runs dozens of times a tile.
 */
const PERIMETER_PLOTS = (() => {
  const plots = [];
  const n = SUBURB.BLOCK_PLOTS;

  for (let px = 0; px < n; px += 1) {
    for (let pz = 0; pz < n; pz += 1) {
      const onEdge = px === 0 || pz === 0 || px === n - 1 || pz === n - 1;
      if (!onEdge) continue;

      /*
        Which way the house faces: outward, toward the street its plot backs onto. Corner plots take the
        nearest edge. This is what makes a street read as a street rather than as two rows of unrelated boxes.
      */
      let facing = 0;
      if (pz === 0) facing = -Math.PI / 2;
      else if (pz === n - 1) facing = Math.PI / 2;
      else if (px === 0) facing = Math.PI;

      plots.push({ px, pz, facing });
    }
  }

  return plots;
})();

/** Ground, and whether it is level enough to build a town on. */
function createTerrain(seed) {
  const field = createFbm(seed, 'elevation:suburbia', {
    octaves: SUBURB.OCTAVES,
    baseFrequency: 1 / SUBURB.WAVELENGTH,
    persistence: SUBURB.PERSISTENCE,
    lacunarity: SUBURB.LACUNARITY,
  });

  return function sampleSuburbElevation(x, z) {
    // Remapped to 0..1 so this biome never contributes a negative elevation to the blended sum.
    return toUnitRange(field(x, z)) * SUBURB.MAX_ELEVATION;
  };
}

/**
 * Build the block rule: what a block holds, from its index alone.
 *
 * Separated from placement so the reservation can consult exactly the same answer without needing the terrain —
 * the pattern the city, the telescope arrays and the airfields all use.
 */
function createBlocks(seed) {
  const blockSeed = deriveSeed(seed, 'structures:suburbia');

  return function blockAt(blockX, blockZ, weight) {
    /*
      One stream per block, keyed by block index rather than by tile, so a town does not depend on which tile is
      being built or the order tiles are visited in.

      Roll order is part of the world's identity. Reordering these, or inserting one before an existing one,
      rebuilds every town at every seed. Append only.
    */
    const random = createLocalRng(blockSeed, blockX, blockZ);

    const civicRoll = random();
    const civicPlaceRoll = random();
    const civicFacingRoll = random();

    /*
      Civic buildings are mutually exclusive and take the whole block, which is what keeps them rare without a
      second placement system. Scaled by weight like everything else, so the edge of a town is houses.
    */
    const churchChance = SUBURB.CHURCH_CHANCE * weight;
    const schoolChance = SUBURB.SCHOOL_CHANCE * weight;
    const towerChance = SUBURB.WATER_TOWER_CHANCE * weight;

    let civic = null;
    if (civicRoll < churchChance) civic = 'church';
    else if (civicRoll < churchChance + schoolChance) civic = 'school';
    else if (civicRoll < churchChance + schoolChance + towerChance) civic = 'waterTower';

    return { civic, civicPlaceRoll, civicFacingRoll, random };
  };
}

export const suburbia = {
  id: 'suburbia',
  label: 'Small town',

  palette: PALETTE.suburbia,

  /**
   * Buildings get a warm sodium ramp where the ground stays cold.
   *
   * The same trick the forest uses for its trees, and the reason is sharper here: this biome sits next to the
   * city, whose towers are cold and bright, and a town drawn in the same colours would read as more of the same
   * rather than as what surrounds it.
   */
  structurePalette: SUBURB_PALETTE,

  suitabilityBias: SUBURB.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * Gentle plain fBm, low amplitude. A town wants ground that is nearly level — not because the code cannot
   * cope with a slope, but because a rectangular street grid on one looks wrong in a way no amount of draping
   * fixes.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number}
   */
  createElevation(seed) {
    return createTerrain(seed);
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: SUBURB.MAX_ELEVATION,

  /**
   * Build the structure placement function: houses, streets and the occasional civic building.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    const blockAt = createBlocks(seed);

    /**
     * @param {{originX: number, originZ: number, size: number}} region
     * @param {object} context
     * @param {(x: number, z: number) => number} context.sampleWeight
     * @param {(x: number, z: number) => number} context.sampleGround - The *drawn* surface, or buildings hover.
     * @param {(x: number, z: number) => object} context.sampleGroundDetail
     * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
     * @returns {Array<object>}
     */
    return function collectStructures(
      region,
      { sampleWeight, sampleGround, sampleGroundDetail, isReserved },
    ) {
      const xRange = blockRange(region.originX, region.size);
      const zRange = blockRange(region.originZ, region.size);

      const structures = [];

      for (let blockX = xRange.first; blockX <= xRange.last; blockX += 1) {
        for (let blockZ = zRange.first; blockZ <= zRange.last; blockZ += 1) {
          const minX = blockX * BLOCK_SIZE;
          const minZ = blockZ * BLOCK_SIZE;
          const centerX = minX + BLOCK_SIZE / 2;
          const centerZ = minZ + BLOCK_SIZE / 2;

          const weight = sampleWeight(centerX, centerZ);
          if (weight <= 0) continue;

          /*
            Slope and clearance are decided for the whole block, before any roll is spent on its contents.

            **Both are one call where a per-house test would be a dozen**, and that is the whole reason this
            biome is affordable: a block is under 300 units across, so a block clear of another biome's
            structures has no house standing in a lake either. Farmland settled the same question the same way
            for its fields.
          */
          if (sampleGroundDetail(centerX, centerZ).slope > SUBURB.MAX_GROUND_SLOPE) continue;

          /*
            Nine small queries over the block rather than one big one, and the difference is not a detail.

            **A clearance test cannot be scaled up by growing its radius.** The water clearance probes an object's
            centre and a ring at its own radius, which is exact for a tree and useless for a block: asked with a
            radius of 300 it checks the centre and a ring 300 out, and sees nothing in between — so a lake sitting
            under the middle of a block's streets went undetected. Farmland learned the same thing about its
            520-unit fields and answered it the same way.

            Each query keeps its radius at about half the grid spacing, so the discs overlap and nothing wider than
            about sixty units can hide between them. A lake is hundreds.
          */
          if (blockIsReserved(minX, minZ, isReserved)) continue;

          const block = blockAt(blockX, blockZ, weight);

          /*
            Streets: only this block's own two low-side edges, so every interior street belongs to exactly one
            block. Emitting all four would draw each one twice, and two coincident lines are twice as bright
            under bloom — the mistake farmland's hedgerows made first.
          */
          const streets = [
            ...streetSegments([minX, minZ], [minX + BLOCK_SIZE, minZ], sampleGround),
            ...streetSegments([minX, minZ], [minX, minZ + BLOCK_SIZE], sampleGround),
          ];

          structures.push({ kind: 'townFeature', x: centerX, z: centerZ, lines: streets, weight });

          if (block.civic) {
            /*
              Placed off-centre in the block by a rolled amount, because a civic building squarely in the middle
              of every block it occupies reads as stamped. Its facing is rolled too, so a church is not always
              square to the grid — which is true of real ones and stops a town looking like a plan.
            */
            const offset = (block.civicPlaceRoll * 2 - 1) * (BLOCK_SIZE * 0.18);
            const x = centerX + offset;
            const z = centerZ - offset;
            const facing = block.civicFacingRoll * Math.PI * 2;

            const placement = { x, z, facing, groundY: sampleGround(x, z) };

            /*
              The school's pitch goes beside the range rather than at a rolled position: a pitch on the far side of
              a street from its school would not read as belonging to it.
            */
            const fieldX = x - Math.sin(facing) * (SUBURB.SCHOOL_WIDTH / 2 + SUBURB.FIELD_WIDTH / 2 + FIELD_GAP);
            const fieldZ = z + Math.cos(facing) * (SUBURB.SCHOOL_WIDTH / 2 + SUBURB.FIELD_WIDTH / 2 + FIELD_GAP);

            /*
              Tested separately from the block, because a civic building reaches past the block's own bounds and the
              nine-probe grid above only covers the block. Two probes rather than one for a school, since its pitch
              is a hundred units clear of the range it belongs to.
            */
            const civicClear =
              !isReserved?.(x, z, CIVIC_RADIUS) &&
              (block.civic !== 'school' || !isReserved?.(fieldX, fieldZ, SUBURB.FIELD_LENGTH / 2));

            if (civicClear) {
              let lines;
              if (block.civic === 'church') lines = churchSegments(placement);
              else if (block.civic === 'school') {
                lines = [
                  ...schoolSegments(placement),
                  ...fieldSegments({ x: fieldX, z: fieldZ, facing }, sampleGround),
                ];
              } else lines = waterTowerSegments(placement);

              structures.push({ kind: 'townFeature', x, z, lines, weight, civic: block.civic });

              // A civic block gives up its houses, which is what makes room for the building and its grounds.
              continue;
            }

            /*
              Refused, so the block falls through to houses rather than being left empty. An empty block in the
              middle of a town would read as a mistake, where houses read as the town simply not having a church
              on that block — which is true of most of them.
            */
          }

          // --- houses --------------------------------------------------------

          const houseChance = SUBURB.HOUSE_CHANCE * weight;

          for (const plot of PERIMETER_PLOTS) {
            /*
              Rolls drawn for every perimeter plot whether or not it is built on, so the pattern of a street does
              not shift when one house is skipped. The block's own stream continues here, which is why
              `blockAt` hands it back rather than closing over it.
            */
            const presenceRoll = block.random();
            const widthRoll = block.random();
            const wallRoll = block.random();
            const roofRoll = block.random();
            const jitterX = block.random();
            const jitterZ = block.random();

            if (presenceRoll >= houseChance) continue;

            const plotCenterX = minX + (plot.px + 0.5) * SUBURB.PLOT_SIZE;
            const plotCenterZ = minZ + (plot.pz + 0.5) * SUBURB.PLOT_SIZE;

            const jitter = SUBURB.PLOT_SIZE * SUBURB.HOUSE_JITTER;
            const x = plotCenterX + (jitterX * 2 - 1) * jitter;
            const z = plotCenterZ + (jitterZ * 2 - 1) * jitter;

            const width = lerp(SUBURB.MIN_HOUSE_WIDTH, SUBURB.MAX_HOUSE_WIDTH, widthRoll);

            structures.push({
              kind: 'gableBuilding',
              centerX: x,
              centerZ: z,
              width,
              depth: width * SUBURB.HOUSE_DEPTH_RATIO,
              groundY: sampleGround(x, z),
              wallHeight: lerp(SUBURB.MIN_WALL_HEIGHT, SUBURB.MAX_WALL_HEIGHT, wallRoll),
              roofHeight: lerp(SUBURB.MIN_ROOF_HEIGHT, SUBURB.MAX_ROOF_HEIGHT, roofRoll),

              /*
                Facing the street the plot backs onto, so a row of houses shares one orientation. The single
                biggest thing that separates a town from a scattering of sheds.
              */
              facing: plot.facing,
              weight,
            });
          }
        }
      }

      return structures;
    };
  },

  /**
   * Publish the town so other biomes keep out of it.
   *
   * A circle over each block rather than a footprint per house: houses are small, dense and numerous, and this
   * question is asked by every tree and rock column in the world. Generous in the safe direction — a tree just
   * inside a block's edge is refused, which costs a tree and keeps conifers out of front gardens.
   *
   * Needs no terrain, so it answers at any coordinate for the price of one biome-field sample.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    return function insideATown(x, z, radius, { sampleWeight, cache }) {
      const margin = radius + SUBURB.CLEARANCE + BLOCK_REACH;

      const firstX = Math.floor((x - margin) / BLOCK_SIZE);
      const lastX = Math.floor((x + margin) / BLOCK_SIZE);
      const firstZ = Math.floor((z - margin) / BLOCK_SIZE);
      const lastZ = Math.floor((z + margin) / BLOCK_SIZE);

      for (let blockX = firstX; blockX <= lastX; blockX += 1) {
        for (let blockZ = firstZ; blockZ <= lastZ; blockZ += 1) {
          const key = `suburb:${blockX},${blockZ}`;

          /*
            Memoised per tile, and not optional. Blocks are under 300 units and trees are far denser than that,
            so without this the same block is re-derived hundreds of times per tile — the cost the telescope
            arrays learned about at five milliseconds a forest tile.
          */
          let built = cache?.get(key);

          if (built === undefined) {
            const weight = sampleWeight(
              (blockX + 0.5) * BLOCK_SIZE,
              (blockZ + 0.5) * BLOCK_SIZE,
            );
            built = weight > 0;
            cache?.set(key, built);
          }

          if (!built) continue;

          /*
            A rectangle, not a circle: blocks tile the plane, so a circle would leave the corners between four
            blocks unreserved while a rectangle covers the town continuously.
          */
          const nearX = Math.max(blockX * BLOCK_SIZE, Math.min(x, (blockX + 1) * BLOCK_SIZE));
          const nearZ = Math.max(blockZ * BLOCK_SIZE, Math.min(z, (blockZ + 1) * BLOCK_SIZE));

          if (Math.hypot(x - nearX, z - nearZ) < radius + SUBURB.CLEARANCE) return true;
        }
      }

      return false;
    };
  },
};

/** Exported so tests and budgets can reason about the grid. */
export { BLOCK_SIZE, PERIMETER_PLOTS };
